import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiClient, getUserInfo } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { checkPermission } from '../../utils/permissionUtils';
import { eventToFormInitialData } from '../../utils/eventFormInitialData';
import { CalendarEvent } from '../../components/Calendar';
import Modal from '../../components/Modal';
import AttendanceOverview from '../../components/AttendanceOverview';
import EventComments from '../../components/messaging/EventComments';
import ExportModal from '../../components/ExportModal';
import { getEventExportFields, ExportableData } from '../../utils/exportUtils';
import { memberFields } from '../../utils/entityFields';
import EventForm, { IEventFormData } from '../../components/forms/EventForm';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';
import { ConfirmModal } from '../../components/ui';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import RequirementsWidget from '../../components/RequirementsWidget';
import EventRequirementsConfig from '../../components/EventRequirementsConfig';
import './AdminEventInvitationsPage.css';
import '../eventCardClassic.css';
import './EventCalendarPage.css';
import { translateError } from '../../utils/errorMessages';

interface ISelectOption {
  _id: string | number;
  name: string;
  preferences?: { themeColor?: string };
  role?: {
    type?: string;
    name: string;
    permissions?: Record<string, number>;
  };
}

interface MemberAttendance {
  _id: string;
  name: string;
  color: string;
  status: 'attending' | 'not-attending' | 'maybe';
  user?: {
    _id: string;
    name: string;
    email: string;
    admin?: boolean;
  };
  groups?: { _id: string; name: string }[];
}

interface GroupAttendance {
  _id: string;
  name: string;
  members: MemberAttendance[];
}

interface AttendanceData {
  eventId: string;
  defaultAttendance: boolean | null;
  groups: GroupAttendance[];
  directMembers: MemberAttendance[];
}

const AdminEventInvitationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  // Permission checks
  const canEdit = hasPermission('events:write');
  const canDelete = hasPermission('events:delete');
  const canViewUsers = hasPermission('users:read');

  // Data state
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection options (pre-fetched for EventForm)
  const [allMembers, setAllMembers] = useState<ISelectOption[]>([]);
  const [allGroups, setAllGroups] = useState<ISelectOption[]>([]);
  const [admins, setAdmins] = useState<ISelectOption[]>([]);

  // Filter state
  const [filterGroup, setFilterGroup] = useState<string>('');
  const [filterMember, setFilterMember] = useState<string>('');

  // Modal state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Escape/backdrop must not silently bin an in-progress event edit.
  const [formDirty, setFormDirty] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'form' | 'attendance' | 'comments'>('form');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form state
  const [formInitialData, setFormInitialData] = useState<Partial<IEventFormData> | undefined>(undefined);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState<ExportableData[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Map<string, boolean | null>>(new Map());
  const [exportLoading, setExportLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.events.list();
      setEvents(data as CalendarEvent[]);
    } catch (err: any) {
      setError(translateError(err, t));
    }
  }, []);

  const fetchSelectOptions = useCallback(async () => {
    try {
      const [membersRes, groupsRes] = await Promise.all([
        api.members.list(),
        api.groups.list(),
      ]);
      setAllMembers(membersRes.data as ISelectOption[]);
      setAllGroups(groupsRes.data as ISelectOption[]);

      // Fetch users if user has permission
      if (canViewUsers) {
        try {
          const usersRes = await api.users.list();
          const usersData = usersRes.data as ISelectOption[];
          // Filter users who can manage events (Admin/Owner role or events:write permission)
          setAdmins(usersData.filter(u =>
            u.role?.type === 'admin' ||
            u.role?.name === 'Owner' ||
            checkPermission(u.role?.permissions, 'events:write')
          ));
        } catch {
          // Ignore error - user might not have permission
        }
      }
    } catch {
      // Silently fail for select options
    }
  }, [canViewUsers]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEvents(), fetchSelectOptions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEvents, fetchSelectOptions]);

  // Filter events: manual only (no template), upcoming only, then by group/member
  const filteredEvents = useMemo(() => {
    const now = new Date();

    let filtered = events.filter((event) => {
      // Only manual events (template is null/undefined/falsy)
      const template = event.template;
      if (template) return false;

      // Only upcoming events
      if (new Date(event.startTime) < now) return false;

      return true;
    });

    // Filter by group: event must have the selected group assigned
    if (filterGroup) {
      filtered = filtered.filter((event) => {
        const eventGroups = (event.groups || []).map((g: any) =>
          typeof g === 'string' ? g : g._id
        );
        return eventGroups.some((id: any) => String(id) === String(filterGroup));
      });
    }

    // Filter by member: event must have the selected member assigned
    if (filterMember) {
      filtered = filtered.filter((event) => {
        const eventMembers = (event.members || []).map((m: any) =>
          typeof m === 'string' ? m : m._id
        );
        return eventMembers.some((id: any) => String(id) === String(filterMember));
      });
    }

    // Sort by startTime ascending (nearest first)
    filtered.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    return filtered;
  }, [events, filterGroup, filterMember]);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isMultiDay = (startStr: string, endStr: string) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    return s.toDateString() !== e.toDateString();
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setFormInitialData(eventToFormInitialData(event));
    setActiveTab('form');
    setModalOpen(true);
  };

  // Consulted by Modal before Escape/backdrop/x dismisses the event form.
  const guardModalClose = (): boolean | void => {
    if (!formDirty) return;
    setDiscardConfirmOpen(true);
    return false;
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFormDirty(false);
    setDiscardConfirmOpen(false);
    setSelectedEvent(null);
    setFormInitialData(undefined);
    setError(null);
    setActiveTab('form');
  };

  const handleUpdateEvent = async (data: IEventFormData) => {
    if (!selectedEvent || !canEdit) return;

    setFormSubmitting(true);
    setError(null);

    try {
      await api.events.update(selectedEvent._id, {
        ...data,
        responsible: data.responsible || null,
      });

      // Refresh events and close modal
      await fetchEvents();
      handleCloseModal();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteEvent = () => {
    if (!selectedEvent || !canDelete) return;
    setDeleteConfirmOpen(true);
  };

  const handleDeleteEventConfirm = async () => {
    if (!selectedEvent || !canDelete) return;
    setDeleteLoading(true);
    try {
      await api.events.delete(selectedEvent._id);
      setDeleteConfirmOpen(false);
      await fetchEvents();
      handleCloseModal();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportClick = async () => {
    if (!selectedEvent) return;

    setExportLoading(true);
    try {
      // Fetch attendance data
      const { data: attendanceData } = await apiClient.get<AttendanceData>(
        `/api/events/${selectedEvent._id}/attendance`
      );

      // Fetch full member data for export
      const { data: fullMembersData } = await api.members.list();

      // Create a map of member ID to full member data
      const memberDataMap = new Map((fullMembersData as any[]).map(m => [m._id, m]));

      // Collect all members from groups and direct members
      const allEventMembers: ExportableData[] = [];
      const attendanceStatusMap = new Map<string, boolean | null>();

      // Add members from groups
      for (const group of attendanceData.groups) {
        for (const member of group.members) {
          const fullMember = memberDataMap.get(member._id);
          if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
            allEventMembers.push(fullMember);
            const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
            attendanceStatusMap.set(member._id, status);
          }
        }
      }

      // Add direct members
      for (const member of attendanceData.directMembers) {
        const fullMember = memberDataMap.get(member._id);
        if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
          allEventMembers.push(fullMember);
          const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
          attendanceStatusMap.set(member._id, status);
        }
      }

      setExportData(allEventMembers);
      setAttendanceMap(attendanceStatusMap);
      setShowExportModal(true);
    } catch (err: any) {
      console.error('Failed to fetch export data:', err);
    } finally {
      setExportLoading(false);
    }
  };

  // Generate export fields from entity definition (includes attendance)
  const exportFields = useMemo(() =>
    getEventExportFields(memberFields, ['name', 'user.name', 'groups', 'attendance']),
  []);

  if (loading) {
    return (
      <div className="admin-invitations-page classic-event-list">
        <LoadingSpinner size="lg" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="admin-invitations-page classic-event-list">
      <div className="page-header">
        <h1>{t('eventInvitations.adminTitle')}</h1>
        <p>{t('eventInvitations.adminSubtitle')}</p>
      </div>

      <div className="invitation-filters">
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}>
          <option value="">{t('eventInvitations.filterByGroup')}</option>
          {allGroups.map(g => (
            <option key={g._id} value={g._id}>{g.name}</option>
          ))}
        </select>
        <select value={filterMember} onChange={e => setFilterMember(e.target.value)}>
          <option value="">{t('eventInvitations.filterByMember')}</option>
          {allMembers.map(m => (
            <option key={m._id} value={m._id}>{m.name}</option>
          ))}
        </select>
      </div>

      {filteredEvents.length === 0 ? (
        <EmptyState
          title={t('eventInvitations.noInvitations')}
          size="sm"
        />
      ) : (
        <div className="events-grid">
          {filteredEvents.map((event) => (
            <div
              key={event._id}
              className="event-card"
              onClick={() => handleEventClick(event)}
            >
              <div className="event-card-content">
                <div className="event-card-header">
                  <h2 className="event-name">{event.name}</h2>
                </div>

                <div className="event-details">
                    <div className="detail-row">
                      <span className="detail-icon">📅</span>
                      <span>
                        {isMultiDay(event.startTime, event.endTime)
                          ? `${formatDate(event.startTime)} – ${formatDate(event.endTime)}`
                          : formatDate(event.startTime)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">⏰</span>
                      <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
                    </div>

                  {event.location && (
                    <div className="detail-row">
                      <span className="detail-icon">📍</span>
                      <span>{event.location}</span>
                    </div>
                  )}

                  {event.price != null && event.price > 0 && (
                    <div className="detail-row">
                      <span className="detail-icon">💰</span>
                      <span className="price">{event.price} CZK</span>
                    </div>
                  )}
                </div>

                {event.description && (
                  <p className="event-description" style={{ whiteSpace: 'pre-line' }}>{event.description}</p>
                )}

                {(event.attendingCount != null || event.totalMembers != null) && (
                  <div className="event-card-stats">
                    <span>{event.attendingCount ?? 0} / {event.totalMembers ?? 0} {t('eventDetails.tabAttendance').toLowerCase()}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onRequestClose={guardModalClose}
        title={selectedEvent ? `${t('common.edit')}: ${selectedEvent.name}` : ''}
        size="xlarge"
      >
        {selectedEvent && (
          <div className="modal-event-edit-container">
            {/* Export Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button variant="secondary" size="sm" onClick={handleExportClick} disabled={exportLoading}>
                {exportLoading ? t('common.loading') : t('export.exportMembers')}
              </Button>
            </div>

            {/* Mobile Tab Switcher */}
            <div className="tab-switcher mobile-only">
              <button
                className={activeTab === 'form' ? 'active' : ''}
                onClick={() => setActiveTab('form')}
              >
                {t('eventDetails.tabEdit')}
              </button>
              <button
                className={activeTab === 'attendance' ? 'active' : ''}
                onClick={() => setActiveTab('attendance')}
              >
                {t('eventDetails.tabAttendance')}
              </button>
              <button
                className={activeTab === 'comments' ? 'active' : ''}
                onClick={() => setActiveTab('comments')}
              >
                {t('eventDetails.tabComments')}
              </button>
            </div>

            <div className="modal-event-edit-layout">
              <div className={`modal-edit-form ${activeTab === 'form' ? 'active' : ''}`}>
                {error && <p className="error-message">{t('common.error')}: {error}</p>}

                <EventForm
                  initialData={formInitialData}
                  onDirtyChange={setFormDirty}
                  onSubmit={handleUpdateEvent}
                  loading={formSubmitting}
                  submitButtonText={t('common.save')}
                  idPrefix="invitations-edit-event"
                  onDelete={canDelete ? handleDeleteEvent : undefined}
                  deleteButtonText={t('common.delete')}
                  prefetchedMembers={allMembers}
                  prefetchedGroups={allGroups}
                  prefetchedAdmins={admins}
                  eventId={selectedEvent?._id}
                />
              </div>

              {/* Widgets column */}
              <div className={`modal-edit-widgets ${activeTab === 'attendance' || activeTab === 'comments' ? 'active' : ''}`}>
                <CollapsibleWidget
                  title={t('eventDetails.tabComments')}
                  defaultExpanded={true}
                  storageKey={`modal-invitation-${selectedEvent._id}-comments`}
                >
                  <EventComments eventId={selectedEvent._id} />
                </CollapsibleWidget>
                <CollapsibleWidget
                  title={t('eventDetails.tabAttendance')}
                  defaultExpanded={true}
                  storageKey={`modal-invitation-${selectedEvent._id}-attendance`}
                >
                  <AttendanceOverview
                    eventId={selectedEvent._id}
                    authToken={getUserInfo()?.token || ''}
                    eventStatus={selectedEvent.status}
                  />
                </CollapsibleWidget>
                <RequirementsWidget eventId={selectedEvent._id} />
                <CollapsibleWidget
                  title={t('requirements.configTitle')}
                  defaultExpanded={false}
                  storageKey={`modal-invitation-${selectedEvent._id}-requirements-config`}
                >
                  <EventRequirementsConfig eventId={selectedEvent._id} />
                </CollapsibleWidget>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename={`event-${selectedEvent?.name.replace(/\s+/g, '-').toLowerCase() || 'members'}-${new Date().toISOString().split('T')[0]}`}
        availableFields={exportFields}
        title={t('export.eventExportTitle')}
        attendanceMap={attendanceMap}
      />

      <ConfirmModal
        isOpen={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={handleCloseModal}
        title={t('unsavedChanges.title')}
        message={t('unsavedChanges.message')}
        confirmText={t('unsavedChanges.discard')}
        cancelText={t('unsavedChanges.keepEditing')}
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteEventConfirm}
        title={t('events.deleteTitle')}
        message={t('events.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminEventInvitationsPage;
